/**
 * Header/Footer Parser — converts OOXML header/footer XML to rich HTML strings
 * for display in the PaginationPlus plugin.
 *
 * OOXML structure:
 *   <w:sectPr>
 *     <w:headerReference w:type="default|first|even" r:id="rIdX"/>
 *     <w:footerReference w:type="default|first|even" r:id="rIdX"/>
 *     <w:titlePage/>  (presence = first page has different header/footer)
 *   </w:sectPr>
 *
 * Header/footer XML files (word/headerN.xml, word/footerN.xml) contain
 * standard OOXML body content: <w:p>, <w:r>, <w:fldSimple>, etc.
 *
 * PaginationPlus expects HTML strings with optional {page} placeholder
 * for auto-incrementing page numbers. We produce rich HTML with full
 * formatting (bold, italic, color, font, size, images, alignment).
 */

import JSZip from "jszip";
import {
  PMNode,
  PMMark,
  ParseContext,
  ParsedHeaderFooterConfig,
  HeaderFooterSection,
  Relationship,
} from "./types";
import { DocxPackage, extractRelationships } from "./zipReader";
import { W_NS, getChildNS } from "./zipReader";
import { parseParagraph } from "./paragraphParser";
import { parseDrawing, parsePict } from "./imageParser";

// Relationships namespace for r:id attributes
const R_NS =
  "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

// ---------------------------------------------------------------------------
// Section properties parsing
// ---------------------------------------------------------------------------

interface SectionReferences {
  defaultHeaderRId?: string;
  firstHeaderRId?: string;
  evenHeaderRId?: string;
  defaultFooterRId?: string;
  firstFooterRId?: string;
  evenFooterRId?: string;
  hasTitlePage: boolean;
}

/**
 * Parse <w:sectPr> to extract header/footer references and titlePage flag.
 */
export function parseSectionReferences(sectPrEl: Element): SectionReferences {
  const refs: SectionReferences = { hasTitlePage: false };

  const children = sectPrEl.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, "");

    if (localName === "headerReference") {
      const type =
        el.getAttributeNS(W_NS, "type") || el.getAttribute("w:type") || "";
      const rId =
        el.getAttributeNS(R_NS, "id") || el.getAttribute("r:id") || "";
      if (type === "default") refs.defaultHeaderRId = rId;
      else if (type === "first") refs.firstHeaderRId = rId;
      else if (type === "even") refs.evenHeaderRId = rId;
    } else if (localName === "footerReference") {
      const type =
        el.getAttributeNS(W_NS, "type") || el.getAttribute("w:type") || "";
      const rId =
        el.getAttributeNS(R_NS, "id") || el.getAttribute("r:id") || "";
      if (type === "default") refs.defaultFooterRId = rId;
      else if (type === "first") refs.firstFooterRId = rId;
      else if (type === "even") refs.evenFooterRId = rId;
    } else if (localName === "titlePage") {
      refs.hasTitlePage = true;
    }
  }

  return refs;
}

// ---------------------------------------------------------------------------
// Header/footer-specific relationship resolution
// ---------------------------------------------------------------------------

/**
 * Load relationships for a specific header/footer part file.
 * E.g., for "word/header1.xml", loads "word/_rels/header1.xml.rels".
 */
async function loadPartRelationships(
  zip: JSZip,
  partPath: string
): Promise<Map<string, Relationship>> {
  // partPath like "word/header1.xml" → "word/_rels/header1.xml.rels"
  const dir = partPath.substring(0, partPath.lastIndexOf("/") + 1);
  const filename = partPath.substring(partPath.lastIndexOf("/") + 1);
  const relsPath = `${dir}_rels/${filename}.rels`;

  const file = zip.file(relsPath);
  if (!file) return new Map();

  try {
    const text = await file.async("text");
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, "text/xml");
    return extractRelationships(doc);
  } catch {
    return new Map();
  }
}

/**
 * Resolve the DOCX part path for a header/footer rId.
 * Looks up the rId in document.xml.rels to get the target (e.g., "header1.xml").
 */
function resolvePartPath(
  docRels: Map<string, Relationship>,
  rId: string
): string | null {
  const rel = docRels.get(rId);
  if (!rel) return null;
  const target = rel.target;
  return target.startsWith("word/") ? target : `word/${target}`;
}

// ---------------------------------------------------------------------------
// Header/footer content parsing
// ---------------------------------------------------------------------------

/**
 * Parse a header or footer XML Document into rich content.
 *
 * Returns a HeaderFooterSection with:
 * - left/right/center HTML strings for PaginationPlus rendering
 * - content PMNode[] for round-trip editing in the mini editor
 *
 * Strategy:
 * 1. Parse all <w:p> through parseParagraph() for full formatting
 * 2. Detect 3-column alignment tables → map to left/center/right
 * 3. Inject {page} for PAGE field codes
 * 4. Resolve images using header-specific relationships
 */
async function parseHeaderFooterXml(
  xmlDoc: Document,
  ctx: ParseContext,
  hfRels: Map<string, Relationship>,
  zip: JSZip,
  messages: string[]
): Promise<HeaderFooterSection | null> {
  const root = xmlDoc.documentElement;
  if (!root) return null;

  // Build a ctx clone with header/footer-specific relationships for image resolution
  const mergedRels = new Map(ctx.relationships);
  // Header/footer rels override document rels for same rId
  hfRels.forEach((val, key) => mergedRels.set(key, val));
  const hfCtx: ParseContext = {
    ...ctx,
    relationships: mergedRels,
  };

  const nodes: PMNode[] = [];
  const children = root.childNodes;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, "");

    if (localName === "p") {
      const parsed = parseParagraphWithFieldCodes(el, hfCtx);
      nodes.push(parsed);
    } else if (localName === "tbl") {
      // Detect 3-column alignment table (common in headers for left/center/right)
      const tableResult = parseAlignmentTable(el, hfCtx);
      if (tableResult) {
        return await resolveImagesInSection(tableResult, hfCtx, zip);
      }
      // Not a 3-column table — parse cells as paragraphs
      const cellNodes = extractTableParagraphs(el, hfCtx);
      nodes.push(...cellNodes);
    }
    // Skip sdt and other elements
  }

  // Resolve image placeholders in the parsed nodes
  const resolvedNodes = await resolveHeaderFooterImages(nodes, hfCtx, zip);

  // Filter out empty paragraphs
  const nonEmptyNodes = resolvedNodes.filter(node => {
    if (node.type === "image") return true;
    if (!node.content || node.content.length === 0) return false;
    // Check if all text content is empty
    const text = extractTextFromPMNodes(node.content);
    return text.trim().length > 0;
  });

  if (nonEmptyNodes.length === 0) return null;

  // Tab-separated single-line layout — the most common non-table Word
  // header ("left<TAB>center<TAB>right" in one paragraph) — splits into
  // zones. 2 segments map to left/right, 3 to left/center/right.
  const tabZones = splitNodesOnTabs(nonEmptyNodes);
  if (tabZones) {
    return {
      left: tabZones.left
        .map(n => pmNodeToHtml(n))
        .join("")
        .trim(),
      center: tabZones.center
        .map(n => pmNodeToHtml(n))
        .join("")
        .trim(),
      right: tabZones.right
        .map(n => pmNodeToHtml(n))
        .join("")
        .trim(),
      content: nonEmptyNodes,
    };
  }

  // Fallback: all content goes into "left"
  const html = nonEmptyNodes.map(n => pmNodeToHtml(n)).join("");

  return {
    left: html,
    right: "",
    content: nonEmptyNodes,
  };
}

/**
 * Detect a tab-separated zone layout: exactly one paragraph whose inline
 * content contains 1–2 tab characters. Returns the inline nodes split into
 * per-zone paragraphs, or null when the layout doesn't match.
 *
 * Exported for unit tests.
 */
export function splitNodesOnTabs(
  nodes: PMNode[]
): { left: PMNode[]; center: PMNode[]; right: PMNode[] } | null {
  if (nodes.length !== 1) return null;
  const paragraph = nodes[0];
  if (paragraph.type !== "paragraph" || !paragraph.content) return null;

  // Split inline content on tab characters (tabs may sit inside text nodes)
  const segments: PMNode[][] = [[]];
  for (const inline of paragraph.content) {
    if (inline.type === "text" && (inline.text || "").includes("\t")) {
      const parts = (inline.text || "").split("\t");
      parts.forEach((part, idx) => {
        if (idx > 0) segments.push([]);
        if (part) {
          segments[segments.length - 1].push({ ...inline, text: part });
        }
      });
    } else {
      segments[segments.length - 1].push(inline);
    }
  }

  if (segments.length < 2 || segments.length > 3) return null;

  // Preserve paragraph styling but drop alignment — zones control position
  const attrs = { ...(paragraph.attrs || {}) };
  delete (attrs as Record<string, unknown>).textAlign;
  const wrap = (inlines: PMNode[]): PMNode[] =>
    inlines.length > 0 ? [{ type: "paragraph", attrs, content: inlines }] : [];

  if (segments.length === 2) {
    return { left: wrap(segments[0]), center: [], right: wrap(segments[1]) };
  }
  return {
    left: wrap(segments[0]),
    center: wrap(segments[1]),
    right: wrap(segments[2]),
  };
}

/**
 * Parse a paragraph, replacing PAGE/NUMPAGES field codes with {page}/{pages} text.
 * Uses the full parseParagraph() pipeline for formatting, then patches field codes.
 */
function parseParagraphWithFieldCodes(pEl: Element, ctx: ParseContext): PMNode {
  // First check for field codes — if present, build a hybrid approach
  const fieldInfo = detectFieldCodes(pEl);

  if (!fieldInfo.hasPageField) {
    // No field codes — use standard parser
    return parseParagraph(pEl, ctx);
  }

  // Has field codes — parse normally but inject {page}/{pages} placeholders
  const node = parseParagraph(pEl, ctx);
  injectFieldPlaceholders(node, fieldInfo);
  return node;
}

interface FieldInfo {
  hasPageField: boolean;
  fieldRanges: Array<{
    startIndex: number;
    endIndex: number;
    replacement: string;
  }>;
}

/**
 * Detect PAGE/NUMPAGES field codes in a paragraph element.
 * Returns field ranges for post-processing.
 */
function detectFieldCodes(pEl: Element): FieldInfo {
  let hasPageField = false;
  const fieldRanges: FieldInfo["fieldRanges"] = [];
  const children = pEl.childNodes;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, "");

    if (localName === "fldSimple") {
      const instr =
        el.getAttributeNS(W_NS, "instr") || el.getAttribute("w:instr") || "";
      if (/\bPAGE\b/i.test(instr)) {
        hasPageField = true;
        fieldRanges.push({ startIndex: i, endIndex: i, replacement: "{page}" });
      } else if (/\bNUMPAGES\b/i.test(instr)) {
        fieldRanges.push({
          startIndex: i,
          endIndex: i,
          replacement: "{pages}",
        });
      }
    } else if (localName === "r") {
      const fldCharEl = getChildNS(el, W_NS, "fldChar");
      if (fldCharEl) {
        const type =
          fldCharEl.getAttributeNS(W_NS, "fldCharType") ||
          fldCharEl.getAttribute("w:fldCharType") ||
          "";
        if (type === "begin") {
          const result = scanComplexField(pEl, i);
          if (result) {
            if (/\bPAGE\b/i.test(result.instrText)) {
              hasPageField = true;
              fieldRanges.push({
                startIndex: i,
                endIndex: result.endIndex,
                replacement: "{page}",
              });
            } else if (/\bNUMPAGES\b/i.test(result.instrText)) {
              fieldRanges.push({
                startIndex: i,
                endIndex: result.endIndex,
                replacement: "{pages}",
              });
            }
            i = result.endIndex;
          }
        }
      }
    }
  }

  return { hasPageField, fieldRanges };
}

/**
 * Inject {page}/{pages} placeholders into parsed PMNode content.
 * The standard parser may have rendered the field's display text (often "1" or empty).
 * We replace it with the placeholder text.
 */
function injectFieldPlaceholders(node: PMNode, fieldInfo: FieldInfo): void {
  if (!node.content || fieldInfo.fieldRanges.length === 0) return;

  // Simple approach: walk the inline content and replace any text that looks
  // like a page number display with {page}. If the paragraph has field codes,
  // the parseParagraph would have produced runs for the display text.
  // Since field codes produce short display text (often "1" or empty),
  // we append {page} if not already present.
  const existingText = extractTextFromPMNodes(node.content);

  // If we already have {page} in the content somehow, skip
  if (existingText.includes("{page}") || existingText.includes("{pages}"))
    return;

  // Replace only the NUMERIC field-display runs with placeholders, preserving
  // surrounding literal text. The old code overwrote the FIRST text node
  // wholesale — so "Page 1 of 3" (runs: "Page ", "1", " of ", "3") became
  // "{page}1 of 3" (the word "Page" lost, on page 5 rendering "51 of 3") and
  // {pages} was never injected. Now: the first numeric run → {page}, and a later
  // numeric run → {pages} when the paragraph also has a NUMPAGES field.
  if (fieldInfo.hasPageField) {
    const hasPagesField = fieldInfo.fieldRanges.some(
      r => r.replacement === "{pages}"
    );
    let replacedPage = false;
    let replacedPages = false;
    for (const child of node.content) {
      if (child.type === "text" && child.text && /^\s*\d+\s*$/.test(child.text)) {
        if (!replacedPage) {
          child.text = "{page}";
          replacedPage = true;
        } else if (hasPagesField && !replacedPages) {
          child.text = "{pages}";
          replacedPages = true;
          break;
        }
      }
    }
    // The PAGE field rendered empty (no numeric display run) — append a token.
    if (!replacedPage) {
      node.content.push({ type: "text", text: "{page}" });
    }
  }
}

// ---------------------------------------------------------------------------
// 3-column alignment table detection
// ---------------------------------------------------------------------------

/**
 * Detect and parse a 3-column alignment table commonly used in Word headers.
 * Word uses these tables to create left/center/right aligned content.
 * Returns a HeaderFooterSection if detected, null otherwise.
 */
function parseAlignmentTable(
  tblEl: Element,
  ctx: ParseContext
): HeaderFooterSection | null {
  // Find all rows
  const rows: Element[] = [];
  const tblChildren = tblEl.childNodes;
  for (let i = 0; i < tblChildren.length; i++) {
    const child = tblChildren[i];
    if (child.nodeType !== 1) continue;
    const localName =
      (child as Element).localName ||
      (child as Element).nodeName.replace(/^w:/, "");
    if (localName === "tr") rows.push(child as Element);
  }

  // Only handle single-row tables with exactly 3 cells
  if (rows.length !== 1) return null;

  const cells: Element[] = [];
  const rowChildren = rows[0].childNodes;
  for (let i = 0; i < rowChildren.length; i++) {
    const child = rowChildren[i];
    if (child.nodeType !== 1) continue;
    const localName =
      (child as Element).localName ||
      (child as Element).nodeName.replace(/^w:/, "");
    if (localName === "tc") cells.push(child as Element);
  }

  if (cells.length !== 3) return null;

  // Parse each cell's content
  const cellContents: PMNode[][] = cells.map(cell => {
    const paragraphs: PMNode[] = [];
    const cellChildren = cell.childNodes;
    for (let i = 0; i < cellChildren.length; i++) {
      const child = cellChildren[i];
      if (child.nodeType !== 1) continue;
      const localName =
        (child as Element).localName ||
        (child as Element).nodeName.replace(/^w:/, "");
      if (localName === "p") {
        paragraphs.push(parseParagraphWithFieldCodes(child as Element, ctx));
      }
    }
    return paragraphs;
  });

  const leftHtml = cellContents[0]
    .map(n => pmNodeToHtml(n))
    .join("")
    .trim();
  const centerHtml = cellContents[1]
    .map(n => pmNodeToHtml(n))
    .join("")
    .trim();
  const rightHtml = cellContents[2]
    .map(n => pmNodeToHtml(n))
    .join("")
    .trim();

  // Table-import path still merges center into left. The 3-zone (.hfz)
  // renderer DOES support center now — returning `center` here is a pending
  // behavior change tracked in TODOS (needs its own round-trip verification).
  const mergedLeft = centerHtml
    ? leftHtml
      ? `${leftHtml} ${centerHtml}`
      : centerHtml
    : leftHtml;

  return {
    left: mergedLeft,
    right: rightHtml,
    content: [...cellContents[0], ...cellContents[1], ...cellContents[2]],
  };
}

/**
 * Extract paragraphs from all cells of a non-alignment table.
 */
function extractTableParagraphs(tblEl: Element, ctx: ParseContext): PMNode[] {
  const result: PMNode[] = [];
  const cells =
    tblEl.getElementsByTagNameNS?.(W_NS, "tc") ||
    tblEl.getElementsByTagName("w:tc");

  for (let i = 0; i < cells.length; i++) {
    const cellChildren = cells[i].childNodes;
    for (let j = 0; j < cellChildren.length; j++) {
      const child = cellChildren[j];
      if (child.nodeType !== 1) continue;
      const localName =
        (child as Element).localName ||
        (child as Element).nodeName.replace(/^w:/, "");
      if (localName === "p") {
        result.push(parseParagraphWithFieldCodes(child as Element, ctx));
      }
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Image resolution for header/footer content
// ---------------------------------------------------------------------------

/**
 * Resolve _drawing/_pict placeholders in header/footer PMNodes.
 * Reuses the same parseDrawing/parsePict functions from imageParser.
 */
async function resolveHeaderFooterImages(
  nodes: PMNode[],
  ctx: ParseContext,
  zip: JSZip
): Promise<PMNode[]> {
  const result: PMNode[] = [];

  for (const node of nodes) {
    if (node.type === "paragraph" || node.type === "heading") {
      if (!node.content) {
        result.push(node);
        continue;
      }

      const hasImages = node.content.some(
        c => c.type === "_drawing" || c.type === "_pict"
      );
      if (!hasImages) {
        result.push(node);
        continue;
      }

      // Split paragraph around images (same logic as main parser)
      let currentInline: PMNode[] = [];
      for (const child of node.content) {
        if (child.type === "_drawing" || child.type === "_pict") {
          if (currentInline.length > 0) {
            result.push({ ...node, content: currentInline });
            currentInline = [];
          }
          const el = child.attrs?._element as Element;
          if (el) {
            const imageNode =
              child.type === "_drawing"
                ? await parseDrawing(el, ctx, zip)
                : await parsePict(el, ctx, zip);
            if (imageNode) result.push(imageNode);
          }
        } else {
          currentInline.push(child);
        }
      }
      if (currentInline.length > 0) {
        result.push({ ...node, content: currentInline });
      }
    } else {
      result.push(node);
    }
  }

  return result;
}

/**
 * Resolve images in a HeaderFooterSection's content array.
 */
async function resolveImagesInSection(
  section: HeaderFooterSection,
  ctx: ParseContext,
  zip: JSZip
): Promise<HeaderFooterSection> {
  if (!section.content) return section;
  const resolved = await resolveHeaderFooterImages(section.content, ctx, zip);
  return {
    ...section,
    content: resolved,
  };
}

// ---------------------------------------------------------------------------
// Complex field scanning (reused from previous implementation)
// ---------------------------------------------------------------------------

/**
 * Scan forward from a fldChar begin to find instrText and the end fldChar.
 */
function scanComplexField(
  pEl: Element,
  startIdx: number
): { instrText: string; endIndex: number } | null {
  let instrText = "";
  let depth = 1;
  const children = pEl.childNodes;

  for (let i = startIdx + 1; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, "");

    if (localName === "r") {
      const fldCharEl = getChildNS(el, W_NS, "fldChar");
      if (fldCharEl) {
        const type =
          fldCharEl.getAttributeNS(W_NS, "fldCharType") ||
          fldCharEl.getAttribute("w:fldCharType") ||
          "";
        if (type === "begin") depth++;
        else if (type === "end") {
          depth--;
          if (depth === 0) return { instrText, endIndex: i };
        }
        continue;
      }

      const instrEl = getChildNS(el, W_NS, "instrText");
      if (instrEl) {
        instrText += instrEl.textContent || "";
      }
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// PMNode → Rich HTML conversion
// ---------------------------------------------------------------------------

function pmNodeToHtml(node: PMNode): string {
  if (node.type === "paragraph") {
    const textContent = renderInlineContent(node.content || []);
    if (!textContent.trim()) return "";

    const styles: string[] = [];
    if (node.attrs?.textAlign)
      styles.push(`text-align: ${node.attrs.textAlign}`);
    if (node.attrs?.marginLeft)
      styles.push(`margin-left: ${node.attrs.marginLeft}pt`);
    if (node.attrs?.marginRight)
      styles.push(`margin-right: ${node.attrs.marginRight}pt`);
    if (node.attrs?.lineHeight && node.attrs.lineHeight !== 1.15) {
      styles.push(`line-height: ${node.attrs.lineHeight}`);
    }

    const styleAttr = styles.length > 0 ? ` style="${styles.join("; ")}"` : "";
    return `<span${styleAttr}>${textContent}</span> `;
  }

  if (node.type === "heading") {
    const textContent = renderInlineContent(node.content || []);
    if (!textContent.trim()) return "";
    const level = Number(node.attrs?.level) || 1;
    // Use bold + size for heading appearance in header/footer
    const fontSize = Math.max(18 - (level - 1) * 2, 12);
    return `<span style="font-weight: bold; font-size: ${fontSize}px">${textContent}</span> `;
  }

  if (node.type === "image") {
    const attrs = node.attrs || {};
    const src = attrs.src as string;
    const width = Number(attrs.width) || 0;
    const height = Number(attrs.height) || 0;
    if (!src) return "";
    const displayWidth = width > 0 ? Math.min(width, 200) : 100;
    const w = ` width="${displayWidth}"`;
    const h =
      height > 0 && width > 0
        ? ` height="${Math.round((height / width) * displayWidth)}"`
        : "";
    return `<img src="${src}"${w}${h} style="vertical-align: middle; max-height: 40px; object-fit: contain" /> `;
  }

  // Fallback
  if (node.content) {
    return node.content.map(pmNodeToHtml).join("");
  }

  return "";
}

function renderInlineContent(nodes: PMNode[]): string {
  return nodes.map(renderInlineNode).join("");
}

function renderInlineNode(node: PMNode): string {
  if (node.type === "text") {
    let html = escapeHtml(node.text || "");
    if (node.marks) {
      for (const mark of node.marks) {
        html = applyMark(html, mark);
      }
    }
    return html;
  }

  if (node.type === "image") {
    return pmNodeToHtml(node);
  }

  if (node.content) {
    return renderInlineContent(node.content);
  }

  return "";
}

function applyMark(html: string, mark: PMMark): string {
  switch (mark.type) {
    case "bold":
      return `<strong>${html}</strong>`;
    case "italic":
      return `<em>${html}</em>`;
    case "underline":
      return `<u>${html}</u>`;
    case "strike":
      return `<s>${html}</s>`;
    case "subscript":
      return `<sub>${html}</sub>`;
    case "superscript":
      return `<sup>${html}</sup>`;
    case "textStyle": {
      // These values come straight from untrusted OOXML attributes and are
      // interpolated into a style attribute — validate each against a strict
      // token shape so a crafted value can't break out of the attribute
      // (stored XSS via DOCX header/footer import).
      const styles: string[] = [];
      const color = safeCssColor(mark.attrs?.color);
      if (color) styles.push(`color: ${color}`);
      const fontSize = safeCssFontSize(mark.attrs?.fontSize);
      if (fontSize) styles.push(`font-size: ${fontSize}`);
      const fontFamily = safeCssFontFamily(mark.attrs?.fontFamily);
      if (fontFamily) styles.push(`font-family: ${fontFamily}`);
      const bg = safeCssColor(mark.attrs?.backgroundColor);
      if (bg) styles.push(`background-color: ${bg}`);
      if (styles.length > 0) {
        return `<span style="${escapeHtml(styles.join("; "))}">${html}</span>`;
      }
      return html;
    }
    default:
      return html;
  }
}

/** Accept #hex (3–8 digits) or a bare CSS color keyword; reject the rest. */
function safeCssColor(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const v = value.trim();
  if (/^#[0-9a-fA-F]{3,8}$/.test(v)) return v;
  if (/^[0-9a-fA-F]{6}$/.test(v)) return `#${v}`;
  if (/^[a-zA-Z]{3,20}$/.test(v)) return v.toLowerCase();
  return null;
}

/** Accept numeric sizes with an optional unit (pt/px/em/rem/%). */
function safeCssFontSize(value: unknown): string | null {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const v = String(value).trim();
  return /^\d{1,3}(\.\d{1,2})?(pt|px|em|rem|%)?$/.test(v) ? v : null;
}

/** Accept word characters, spaces, commas and hyphens (quoted families). */
function safeCssFontFamily(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const v = value.trim().replace(/['"]/g, "");
  return /^[\w\s,-]{1,100}$/.test(v) ? v : null;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function extractTextFromPMNodes(nodes: PMNode[]): string {
  let text = "";
  for (const node of nodes) {
    if (node.type === "text") text += node.text || "";
    if (node.content) text += extractTextFromPMNodes(node.content);
  }
  return text;
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

/**
 * Parse headers and footers from a DOCX package.
 *
 * Now async to support image resolution in header/footer content.
 *
 * @param pkg - The DOCX package with extracted header/footer XMLs
 * @param ctx - The parse context (for style resolution)
 * @param sectPrEl - The <w:sectPr> element from the document body
 * @param messages - Warnings/messages array
 * @returns Parsed header/footer configuration for PaginationPlus
 */
export async function parseHeadersAndFooters(
  pkg: DocxPackage,
  ctx: ParseContext,
  sectPrEl: Element | null,
  messages: string[]
): Promise<ParsedHeaderFooterConfig | undefined> {
  if (!sectPrEl) return undefined;

  const refs = parseSectionReferences(sectPrEl);

  const hasAnyRef =
    refs.defaultHeaderRId ||
    refs.firstHeaderRId ||
    refs.defaultFooterRId ||
    refs.firstFooterRId;
  if (!hasAnyRef) return undefined;

  // Resolve document-level relationships for finding header/footer part paths
  const docRels = extractRelationships(pkg.relationshipsXml);

  const config: ParsedHeaderFooterConfig = {
    hasTitlePage: refs.hasTitlePage,
  };

  // Helper to parse a single header or footer
  const parseOne = async (
    rId: string,
    xmls: Map<string, Document>
  ): Promise<HeaderFooterSection | null> => {
    if (!rId || !xmls.has(rId)) return null;

    // Load part-specific relationships for image resolution
    const partPath = resolvePartPath(docRels, rId);
    const hfRels = partPath
      ? await loadPartRelationships(pkg.zip, partPath)
      : new Map<string, Relationship>();

    return parseHeaderFooterXml(xmls.get(rId)!, ctx, hfRels, pkg.zip, messages);
  };

  // Parse all headers/footers
  if (refs.defaultHeaderRId) {
    const section = await parseOne(refs.defaultHeaderRId, pkg.headerXmls);
    if (section) config.defaultHeader = section;
  }
  if (refs.firstHeaderRId) {
    const section = await parseOne(refs.firstHeaderRId, pkg.headerXmls);
    if (section) config.firstPageHeader = section;
  }
  if (refs.defaultFooterRId) {
    const section = await parseOne(refs.defaultFooterRId, pkg.footerXmls);
    if (section) config.defaultFooter = section;
  }
  if (refs.firstFooterRId) {
    const section = await parseOne(refs.firstFooterRId, pkg.footerXmls);
    if (section) config.firstPageFooter = section;
  }

  return config;
}
